import React, { useEffect, useMemo, useState } from "react";

// =============================================
// نور الحديث — Минималистичная игра для изучения хадисов
// Полный самостоятельный файл App.jsx
// Не требует Tailwind/библиотек — всё на inline-стилях
// =============================================

// ----- ДАННЫЕ -----
const HADITHS = [
  {
    id: "niyyah",
    ar: "إِنَّمَا الْأَعْمَالُ بِالنِّيَّاتِ",
    ru: "Поистине, дела (оцениваются) по намерениям.",
    source: "Бухари, Муслим",
    note: "Ключ к искренности: оценивается не внешнее, а намерение (нийя).",
    keywords: ["الأعمال", "النيات"],
  },
  {
    id: "advice",
    ar: "الدِّينُ النَّصِيحَةُ",
    ru: "Религия — это искренний совет.",
    source: "Муслим",
    note: "Относись к людям с искренним желанием принести пользу.",
    keywords: ["الدين", "النصيحة"],
  },
  {
    id: "speakgood",
    ar: "مَنْ كَانَ يُؤْمِنُ بِاللَّهِ وَالْيَوْمِ الْآخِرِ فَلْيَقُلْ خَيْرًا أَوْ لِيَصْمُتْ",
    ru: "Кто верует в Аллаха и Судный день — пусть говорит благое или молчит.",
    source: "Бухари, Муслим",
    note: "Фильтр речи: если сомневаешься — промолчи.",
    keywords: ["ليقل", "خيرًا", "ليصمت"],
  },
  {
    id: "love",
    ar: "الْمَرْءُ مَعَ مَنْ أَحَبَّ",
    ru: "Человек будет с теми, кого он любит.",
    source: "Бухари, Муслим",
    note: "Любовь к праведным — путь к их обществу.",
    keywords: ["المرء", "أحب"],
  },
  {
    id: "harm",
    ar: "لَا ضَرَرَ وَلَا ضِرَارَ",
    ru: "Нет вреда и ответного вредительства.",
    source: "Ибн Маджа и др.",
    note: "Принцип фихха: не причиняй вред и не отвечай вредом.",
    keywords: ["ضرر", "ضرار"],
  },
  {
    id: "brotherhood",
    ar: "الْمُسْلِمُ أَخُو الْمُسْلِمِ لَا يَظْلِمُهُ وَلَا يَخْذُلُهُ",
    ru: "Мусульманин — брат мусульманину: не притесняет и не оставляет без помощи.",
    source: "Бухари, Муслим",
    note: "Поддержка и справедливость к брату.",
    keywords: ["أخو", "لا يظلمه", "لا يخذله"],
  },
  {
    id: "modesty",
    ar: "الْحَيَاءُ شُعْبَةٌ مِنَ الْإِيمَانِ",
    ru: "Скромность — отрасль веры.",
    source: "Бухари, Муслим",
    note: "Развивай скромность как часть имана.",
    keywords: ["الحياء", "الإيمان"],
  },
  {
    id: "ease",
    ar: "إِنَّ الدِّينَ يُسْرٌ",
    ru: "Воистину, религия — лёгкость.",
    source: "Бухари",
    note: "Ислам — о балансе, а не о непосильном.",
    keywords: ["الدين", "يسر"],
  },
];

// ----- SRS/ХРАНИЛИЩЕ -----
const STORAGE_KEY = "hadithSRS_v1";
const HAS_WINDOW = typeof window !== "undefined" && typeof window.localStorage !== "undefined";

function loadState() {
  if (!HAS_WINDOW) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state) {
  if (!HAS_WINDOW) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function initSRS() {
  const now = Date.now();
  const map = {};
  HADITHS.forEach((h) => {
    map[h.id] = { id: h.id, ease: 2.5, interval: 0, reps: 0, due: now, streak: 0 };
  });
  return { items: map, createdAt: now, lights: 0 };
}

function nextInterval({ ease, interval, rating }) {
  const m = 60 * 1000;
  const d = 24 * 60 * m;
  if (rating === "again") return { interval: 10 * m, ease: Math.max(1.3, ease - 0.2) };
  if (rating === "good") return { interval: interval ? interval * 2 : d, ease };
  if (rating === "easy") return { interval: interval ? interval * 3 : 3 * d, ease: Math.min(3.0, ease + 0.05) };
  return { interval: d, ease };
}

// ----- УТИЛЫ/СТИЛИ -----
const styles = {
  page: { minHeight: "100vh", background: "#f6f6f6", color: "#111", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial" },
  container: { maxWidth: 800, margin: "0 auto", padding: 16 },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: 12 },
  title: { fontSize: 20, fontWeight: 700, letterSpacing: 0.2 },
  lightsWrap: { display: "flex", alignItems: "center", gap: 8, opacity: 0.8, fontSize: 14 },
  lightsDots: { display: "flex", gap: 4 },
  dot: { width: 8, height: 8, borderRadius: 8, background: "#fbbf24", boxShadow: "0 0 0 1px #fde68a" },
  pill: (active) => ({ padding: "6px 10px", borderRadius: 999, fontSize: 14, border: "1px solid", borderColor: active ? "#111" : "#ddd", background: active ? "#111" : "#fff", color: active ? "#fff" : "#111", cursor: "pointer" }),
  row: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  card: { borderRadius: 16, border: "1px solid #e5e5e5", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 20 },
  btn: { padding: "8px 12px", borderRadius: 10, border: "1px solid #ddd", background: "#fff", cursor: "pointer" },
  btnPrimary: { padding: "8px 12px", borderRadius: 10, border: "1px solid #111", background: "#111", color: "#fff", cursor: "pointer" },
  hint: { fontSize: 14, color: "#444", background: "rgba(255,255,255,0.9)", border: "1px solid #e5e5e5", borderRadius: 12, padding: "8px 12px" },
  small: { fontSize: 12, color: "#666" },
};

function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ----- КОМПОНЕНТЫ UI -----
function Header({ lights }) {
  const dots = Math.min(10, Math.max(0, Math.floor(lights)));
  return (
    <div style={styles.header}>
      <div style={styles.title}>نور الحديث</div>
      <div style={styles.lightsWrap}>
        <span>Свет:</span>
        <div style={styles.lightsDots}>
          {Array.from({ length: dots }).map((_, i) => (
            <span key={i} style={styles.dot} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ children }) { return <div style={styles.card}>{children}</div>; }
function Hint({ children }) { return <div style={styles.hint}>{children}</div>; }

// ----- РЕЖИМЫ -----
function LearnView({ hadiths, onLight }) {
  const [idx, setIdx] = useState(0);
  const h = hadiths[idx % hadiths.length];
  const [showTr, setShowTr] = useState(true);
  const [showNote, setShowNote] = useState(false);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Hint>Режим изучения: читай хадис, перевод и заметку. Готов — нажимай «Далее».</Hint>
      <Card>
        <div style={{ textAlign: "center", display: "grid", gap: 8 }}>
          <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.6 }} dir="rtl">{h.ar}</div>
          {showTr && <div style={{ color: "#333" }}>{h.ru}</div>}
          <div style={styles.small}>Источник: {h.source}</div>
          {showNote && <div style={{ fontSize: 14, color: "#065f46" }}>{h.note}</div>}
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
          <button style={styles.btn} onClick={() => setShowTr((s) => !s)}>
            {showTr ? "Скрыть перевод" : "Показать перевод"}
          </button>
          <button style={styles.btn} onClick={() => setShowNote((s) => !s)}>
            {showNote ? "Скрыть пояснение" : "Пояснение"}
          </button>
          <button
            style={styles.btnPrimary}
            onClick={() => {
              setIdx((i) => i + 1);
              onLight?.();
            }}
          >
            Далее
          </button>
        </div>
      </Card>
    </div>
  );
}

function MCQView({ hadiths, onResult }) {
  const [q, setQ] = useState(() => pickRandom(hadiths));
  const options = useMemo(() => {
    const wrong = hadiths.filter((h) => h.id !== q.id);
    const shuffled = [q.ru, ...wrong.sort(() => 0.5 - Math.random()).slice(0, 3).map((x) => x.ru)].sort(() => 0.5 - Math.random());
    return shuffled;
  }, [q, hadiths]);
  const [picked, setPicked] = useState(null);
  const isCorrect = picked && picked === q.ru;

  function reset() { setQ(pickRandom(hadiths)); setPicked(null); }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Hint>Режим теста: выбери правильный перевод к арабскому тексту.</Hint>
      <Card>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 600 }} dir="rtl">{q.ar}</div>
            <div style={{ ...styles.small, marginTop: 4 }}>Источник: {q.source}</div>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {options.map((opt, i) => {
              const base = { textAlign: "left", padding: "10px 12px", borderRadius: 12, border: "1px solid #ddd", cursor: "pointer" };
              let ui = base;
              if (picked !== null) {
                if (opt === q.ru) ui = { ...base, borderColor: "#10b981", background: "#ecfdf5" };
                else if (picked === opt) ui = { ...base, borderColor: "#f43f5e", background: "#fff1f2" };
                else ui = { ...base, opacity: 0.7 };
              }
              return (
                <button key={i} style={ui} onClick={() => setPicked(opt)}>
                  {opt}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 14 }}>{picked === null ? "Выбери перевод" : isCorrect ? "Верно!" : "Неверно"}</div>
            {picked !== null && (
              <button style={styles.btn} onClick={() => { onResult?.(isCorrect); reset(); }}>
                Далее
              </button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function ClozeView({ hadiths, onResult }) {
  const [q, setQ] = useState(() => pickRandom(hadiths));
  const [missingIdx, setMissingIdx] = useState(() => Math.floor(Math.random() * q.keywords.length));
  const missing = q.keywords[missingIdx];
  const [answer, setAnswer] = useState("");
  const [checked, setChecked] = useState(false);

  function reset() {
    const nxt = pickRandom(hadiths);
    setQ(nxt);
    setMissingIdx(Math.floor(Math.random() * nxt.keywords.length));
    setAnswer("");
    setChecked(false);
  }

  const masked = q.ar.replace(missing, "▢▢▢");
  const correct = answer.trim() === missing;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Hint>Режим клоз: вставь пропущенное слово (на арабском). Подсказка внизу.</Hint>
      <Card>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 600 }} dir="rtl">{masked}</div>
            <div style={{ ...styles.small, marginTop: 4 }}>Подсказка: {q.ru}</div>
          </div>
          <input
            style={{ border: "1px solid #ddd", borderRadius: 10, padding: "8px 10px" }}
            placeholder="Напиши слово здесь"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            dir="rtl"
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button style={styles.btn} onClick={() => setChecked(true)}>Проверить</button>
            {checked && (
              <div style={{ fontSize: 14, color: correct ? "#059669" : "#be123c" }}>
                {correct ? "Верно!" : `Неверно. Правильно: ${missing}`}
              </div>
            )}
            {checked && (
              <button style={styles.btn} onClick={() => { onResult?.(correct); reset(); }}>
                Далее
              </button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function ReviewView({ hadiths, srs, setSrs }) {
  const dueIds = useMemo(() => {
    const now = Date.now();
    return Object.values(srs.items).filter((it) => it.due <= now).map((it) => it.id);
  }, [srs]);

  const current = useMemo(() => {
    if (dueIds.length === 0) return null;
    const id = pickRandom(dueIds);
    return hadiths.find((h) => h.id === id);
  }, [dueIds, hadiths]);

  function rate(rating) {
    if (!current) return;
    const it = srs.items[current.id];
    const { interval, ease } = nextInterval({ ease: it.ease, interval: it.interval, rating });
    const now = Date.now();
    const updated = { ...it, ease, interval, reps: it.reps + 1, streak: rating === "again" ? 0 : it.streak + 1, due: now + interval };
    const lightsDelta = rating === "again" ? 0 : rating === "good" ? 0.5 : 1;
    const next = { ...srs, items: { ...srs.items, [current.id]: updated }, lights: (srs.lights || 0) + lightsDelta };
    setSrs(next);
    saveState(next);
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Hint>Повторение (SRS): карточки, которые «должны» сегодня. Оцени сложность: «Снова», «Хорошо», «Легко».</Hint>
      {!current ? (
        <Card>
          <div style={{ textAlign: "center", color: "#555" }}>Сегодня повторений нет. Займись изучением или тестами.</div>
        </Card>
      ) : (
        <Card>
          <div style={{ display: "grid", gap: 8, textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 600 }} dir="rtl">{current.ar}</div>
            <div style={{ color: "#222" }}>{current.ru}</div>
            <div style={styles.small}>Источник: {current.source}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              <button style={styles.btn} onClick={() => rate("again")}>Снова</button>
              <button style={styles.btn} onClick={() => rate("good")}>Хорошо</button>
              <button style={styles.btnPrimary} onClick={() => rate("easy")}>Легко</button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ----- ПРИЛОЖЕНИЕ -----
export default function App() {
  const [mode, setMode] = useState("learn"); // learn | mcq | cloze | review
  const [state, setState] = useState(() => loadState() || initSRS());
  const [score, setScore] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    if (HAS_WINDOW && !loadState()) saveState(state);
  }, []);

  function handleResult(ok) {
    setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), total: s.total + 1 }));
    if (ok) { const next = { ...state, lights: (state.lights || 0) + 0.3 }; setState(next); saveState(next); }
  }

  return (
    <div style={styles.page}>
      <Header lights={state.lights || 0} />
      <div style={styles.container}>
        <div style={styles.row}>
          {[
            { key: "learn", label: "Изучение" },
            { key: "mcq", label: "Тест" },
            { key: "cloze", label: "Клоз" },
            { key: "review", label: "Повторение (SRS)" },
          ].map((m) => (
            <button key={m.key} style={styles.pill(mode === m.key)} onClick={() => setMode(m.key)}>
              {m.label}
            </button>
          ))}
          <div style={{ marginLeft: "auto", fontSize: 14, padding: "4px 8px", borderRadius: 999, background: "#fff", border: "1px solid #e5e5e5" }}>
            Счёт: {score.correct}/{score.total}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          {mode === "learn" && (
            <LearnView hadiths={HADITHS} onLight={() => { const next = { ...state, lights: (state.lights || 0) + 0.2 }; setState(next); saveState(next); }} />
          )}
          {mode === "mcq" && <MCQView hadiths={HADITHS} onResult={handleResult} />}
          {mode === "cloze" && <ClozeView hadiths={HADITHS} onResult={handleResult} />}
          {mode === "review" && (
            <ReviewView hadiths={HADITHS} srs={state} setSrs={(v) => { setState(v); saveState(v); }} />
          )}
        </div>

        <div style={{ textAlign: "center", fontSize: 12, color: "#aaa", padding: 16 }}>
          Прототип. Для релиза: расширить базу хадисов, добавить озвучку и статистику.
        </div>
      </div>
    </div>
  );
}
